import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Ticket, BarChart2, Settings, NotebookPen, User, MessageSquare, Users } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { httpClient } from '../../services/httpClient';

const getNavItems = (t: any) => [
  { icon: LayoutDashboard, label: t('navigation.dashboard'), path: '/dashboard' },
  { icon: Ticket, label: t('navigation.ticketWall'), path: '/tickets' },
  { icon: MessageSquare, label: t('navigation.atrium'), path: '/atrium' },
  { icon: Users, label: 'Customers', path: '/customers' },
  { icon: User, label: t('navigation.myWorkspace'), path: '/my-workspace' },
  { icon: NotebookPen, label: t('navigation.myMemos'), path: '/my-memo' },
  { icon: BarChart2, label: t('navigation.reports'), path: '/reports' },
  { icon: Settings, label: t('navigation.settings'), path: '/settings' },
];

export default function Sidebar() {
  const location = useLocation();
  const { user } = useAuthContext();
  const { theme } = useTheme();
  const { t } = useTranslation();
  
  const navItems = getNavItems(t);
  
  // Check if user has access to reports
  const canAccessReports = user?.role === 'admin' || user?.role === 'agent';

  const canAccessSettings = user?.role === 'admin';

  // Check if user has omnichannel access
  const [hasOmnichannelAccess, setHasOmnichannelAccess] = useState(false);
  const [omnichannelCheckComplete, setOmnichannelCheckComplete] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkOmnichannelAccess = async () => {
      try {
        const response = await httpClient.get<{ success: boolean; has_omnichannel_access: boolean }>(
          '/api/channels/permissions'
        );
        if (response.success) {
          setHasOmnichannelAccess(response.has_omnichannel_access);
        }
      } catch (error) {
        console.error('Failed to check omnichannel access:', error);
        setHasOmnichannelAccess(false);
      } finally {
        setOmnichannelCheckComplete(true);
      }
    };

    if (user) {
      checkOmnichannelAccess();
    }
  }, [user]);

  useEffect(() => {
    setIsAdmin(user?.role === 'admin');
  }, [user]);

  // Determine active menu item based on path and source route
  const getIsActive = (itemPath: string) => {
    // If we're on a ticket detail page, check the source route
    if (location.pathname.startsWith('/tickets/') && location.state?.from) {
      return location.state.from === itemPath;
    }
    // Otherwise use the regular path check
    return location.pathname.startsWith(itemPath);
  };

  return (
    <div className={`h-screen w-40 md:w-44 transition-all duration-300 ease-in-out ${
      theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    } border-r`}>
      <div className="p-4">
        <Link to="/" className="block">
          <div className="flex items-center gap-2">
            <img src="/favicon.png" alt={t('common.appLogo')} className="h-8 w-auto" />
            <span className={`text-2xl font-bold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
              FlowCXP
            </span>
          </div>
        </Link>
      </div>
      <nav className="mt-4">
        {navItems.filter((item) => {
          if (item.path === '/reports') return canAccessReports;
          if (item.path === '/settings') return canAccessSettings;
          if (item.path === '/atrium') {
            // Only show if check is complete and user has access or user is admin
            return omnichannelCheckComplete && (hasOmnichannelAccess || isAdmin);
          }
          return true;  
        }).map((item) => {
          const Icon = item.icon;
          const isActive = getIsActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center px-4 py-2 text-sm ${
                isActive
                  ? theme === 'dark'
                    ? 'bg-blue-900 text-blue-300'
                    : 'bg-blue-50 text-blue-600'
                  : theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:opacity-80'
                    : 'text-gray-600 hover:bg-gray-50 hover:opacity-80'
              }`}
            >
              <Icon className="h-5 w-5 mr-3" />
              <span>{item.label}</span>
            </Link>
          );
        })}


      </nav>
    </div>
  );
}